// store/modules/ads.js - Dedicated ads store module

const state = {
    // Array of all fetched ads
    ads: [],

    // Cache ads by location and entity for quick retrieval
    adsCache: new Map(),

    // Track what locations we've fetched to prevent duplicate API calls
    fetchHistory: new Set(),

    // Track which ads have been rendered
    renderedAds: new Set(),

    // Current entity configuration
    entityConfig: {
        entityType: 'tenant',
        entityId: null,
        publisherId: null
    },

    // Loading states
    loading: {
        gallery: false,
        srp_header: false,
        srp_footer: false,
        ldp_header: false,
        ldp_sidebar: false
    },

    // Error states
    errors: {}
};

const mutations = {
    SET_ENTITY_CONFIG(state, { entityType, entityId, publisherId }) {
        state.entityConfig = { entityType, entityId, publisherId };
    },

    ADD_AD(state, ad) {
        // Prevent duplicate ads by checking ID in HTML
        const existingIndex = state.ads.findIndex((existingAd) => {
            if (existingAd.html && ad.html) {
                const existingMatch = existingAd.html.match(/id=['"]([^'"]+)['"]/);
                const newMatch = ad.html.match(/id=['"]([^'"]+)['"]/);
                if (existingMatch && newMatch && existingMatch[1] === newMatch[1]) {
                    return true;
                }
            }
            // Fallback: check location and callback
            return existingAd.location === ad.location && existingAd.callback === ad.callback;
        });

        if (existingIndex === -1) {
            // Add new ad with metadata
            state.ads.push({
                ...ad,
                id: ad.html ? ad.html.match(/id=['"]([^'"]+)['"]/)?.[1] : null,
                fetchedAt: Date.now(),
                rendered: false
            });
        } else {
            // Update existing ad
            state.ads.splice(existingIndex, 1, {
                ...state.ads[existingIndex],
                ...ad,
                fetchedAt: Date.now()
            });
        }
    },

    ADD_MULTIPLE_ADS(state, ads) {
        ads.forEach((ad) => {
            // Use the ADD_AD mutation logic for each ad
            const existingIndex = state.ads.findIndex((existingAd) => {
                if (existingAd.html && ad.html) {
                    const existingMatch = existingAd.html.match(/id=['"]([^'"]+)['"]/);
                    const newMatch = ad.html.match(/id=['"]([^'"]+)['"]/);
                    if (existingMatch && newMatch && existingMatch[1] === newMatch[1]) {
                        return true;
                    }
                }
                return existingAd.location === ad.location && existingAd.callback === ad.callback;
            });

            if (existingIndex === -1) {
                state.ads.push({
                    ...ad,
                    id: ad.html ? ad.html.match(/id=['"]([^'"]+)['"]/)?.[1] : null,
                    fetchedAt: Date.now(),
                    rendered: false
                });
            }
        });
    },

    MARK_AD_RENDERED(state, adId) {
        const ad = state.ads.find((ad) => ad.id === adId);
        if (ad) {
            ad.rendered = true;
            state.renderedAds.add(adId);
        }
    },

    MARK_LOCATION_FETCHED(state, { location, entityType, entityId }) {
        const fetchKey = `${entityType}_${entityId}_${location}`;
        state.fetchHistory.add(fetchKey);
    },

    SET_LOADING(state, { location, loading }) {
        if (state.loading.hasOwnProperty(location)) {
            state.loading[location] = loading;
        }
    },

    SET_ERROR(state, { location, error }) {
        state.errors[location] = error;
    },

    CLEAR_ERROR(state, location) {
        delete state.errors[location];
    },

    CACHE_ADS_FOR_LOCATION(state, { location, entityType, entityId, ads }) {
        const cacheKey = `${entityType}_${entityId}_${location}`;
        state.adsCache.set(cacheKey, {
            ads,
            fetchedAt: Date.now(),
            location
        });
    },

    CLEAR_ADS(state) {
        state.ads = [];
        state.adsCache.clear();
        state.fetchHistory.clear();
        state.renderedAds.clear();
        state.errors = {};
        // Reset loading states
        Object.keys(state.loading).forEach((key) => {
            state.loading[key] = false;
        });
    },

    CLEAR_ADS_FOR_LOCATION(state, location) {
        // Remove ads for specific location
        state.ads = state.ads.filter((ad) => ad.location !== location);

        // Clear from cache
        const keysToDelete = [];
        state.adsCache.forEach((value, key) => {
            if (key.endsWith(`_${location}`)) {
                keysToDelete.push(key);
            }
        });
        keysToDelete.forEach((key) => state.adsCache.delete(key));

        // Clear from fetch history
        const fetchKeysToDelete = [];
        state.fetchHistory.forEach((key) => {
            if (key.endsWith(`_${location}`)) {
                fetchKeysToDelete.push(key);
            }
        });
        fetchKeysToDelete.forEach((key) => state.fetchHistory.delete(key));

        // Clear errors
        delete state.errors[location];

        // Reset loading state
        if (state.loading.hasOwnProperty(location)) {
            state.loading[location] = false;
        }
    },

    RESET_RENDERED_STATE(state) {
        // Reset rendered state without clearing ads (useful for pagination)
        state.ads.forEach((ad) => {
            ad.rendered = false;
        });
        state.renderedAds.clear();
    }
};

const actions = {
    setEntityConfig({ commit }, config) {
        commit('SET_ENTITY_CONFIG', config);
    },

    addAd({ commit }, ad) {
        commit('ADD_AD', ad);
    },

    addMultipleAds({ commit }, ads) {
        commit('ADD_MULTIPLE_ADS', ads);
    },

    markAdRendered({ commit }, adId) {
        commit('MARK_AD_RENDERED', adId);
    },

    markLocationFetched({ commit, state }, location) {
        const { entityType, entityId } = state.entityConfig;
        commit('MARK_LOCATION_FETCHED', { location, entityType, entityId });
    },

    setLoading({ commit }, { location, loading }) {
        commit('SET_LOADING', { location, loading });
    },

    setError({ commit }, { location, error }) {
        commit('SET_ERROR', { location, error });
    },

    clearError({ commit }, location) {
        commit('CLEAR_ERROR', location);
    },

    clearAds({ commit }) {
        commit('CLEAR_ADS');
    },

    clearAdsForLocation({ commit }, location) {
        commit('CLEAR_ADS_FOR_LOCATION', location);
    },

    resetRenderedState({ commit }) {
        commit('RESET_RENDERED_STATE');
    },

    // Cache ads for a specific location
    cacheAdsForLocation({ commit, state }, { location, ads }) {
        const { entityType, entityId } = state.entityConfig;
        commit('CACHE_ADS_FOR_LOCATION', { location, entityType, entityId, ads });
    },

    // Check if we already fetched ads for a location
    wasLocationFetched({ state }, location) {
        const { entityType, entityId } = state.entityConfig;
        const fetchKey = `${entityType}_${entityId}_${location}`;
        return state.fetchHistory.has(fetchKey);
    },

    // Get cached ads for a location
    getCachedAds({ state }, location) {
        const { entityType, entityId } = state.entityConfig;
        const cacheKey = `${entityType}_${entityId}_${location}`;
        const cached = state.adsCache.get(cacheKey);

        if (cached) {
            // Check if cache is still fresh (5 minutes)
            const maxAge = 5 * 60 * 1000;
            if (Date.now() - cached.fetchedAt < maxAge) {
                return cached.ads;
            }
        }

        return null;
    },

    getAdByLocation: (state) => (location) => {
        return state.ads.find((ad) => ad.location === location) || null;
    },

    getAdIdByLocation: (state, getters) => (location) => {
        const ad = getters.getAdByLocation(location);
        return ad ? ad.id : null;
    },

    getAdByLocationAndIndex: (state) => (location, index) => {
        const adsAtLocation = state.ads.filter((ad) => ad.location === location);
        return adsAtLocation[index] || null;
    },
    getAdsCountByLocation: (state) => (location) => {
        return state.ads.filter((ad) => ad.location === location).length;
    },

    // Returns the array of ad positions for the gallery, given resultMinCount and resultMaxCount
    galleryAdsPositions: (state, getters) => (resultMinCount, resultMaxCount) => {
        const galleryAdsCount = Math.min(getters.galleryAds.length, 2); // Cap at 2 ads for small sets
        const totalProperties = resultMaxCount - resultMinCount + 1;
        if (galleryAdsCount === 0 || totalProperties < 1) return [];

        // For small result sets, use hand-tuned positions for up to 2 ads
        if (totalProperties <= 7) {
            if (galleryAdsCount === 1) return [3];
            if (galleryAdsCount === 2) return [2, 5];
            return [];
        }

        // For larger result sets, spread as before
        const positions = [];
        const minStart = 3;
        const maxEnd = totalProperties - 1;
        const adsToShow = getters.galleryAds.length;
        if (adsToShow === 1) {
            positions.push(minStart);
        } else {
            for (let i = 0; i < adsToShow; i++) {
                const pos = Math.round(minStart + (i * (maxEnd - minStart)) / (adsToShow - 1));
                positions.push(pos);
            }
        }
        return Array.from(new Set(positions)).filter((p) => p >= minStart && p <= maxEnd);
    },

    // Returns the ad for a given index, using the above positions
    getGalleryAdForPosition: (state, getters) => (index, resultMinCount, resultMaxCount) => {
        const adsPositions = getters.galleryAdsPositions(resultMinCount, resultMaxCount);
        const galleryAds = getters.galleryAds;
        const positionInAds = adsPositions.indexOf(index);
        if (positionInAds !== -1 && galleryAds && galleryAds[positionInAds]) {
            return galleryAds[positionInAds];
        }
        return null;
    },

    getGalleryAdIndex: (state, getters) => (index) => {
        const galleryAds = getters.galleryAds;
        const positionInAds = galleryAds.indexOf(index);
        return positionInAds;
    }
};

const getters = {
    // Get all ads
    allAds: (state) => state.ads,

    // Get ads by location
    adsByLocation: (state) => (location) => {
        return state.ads.filter((ad) => ad.location === location);
    },

    // Get gallery ads specifically
    galleryAds: (state) => {
        return state.ads.filter((ad) => ad.location === 'gallery');
    },

    // Get rendered ads
    renderedAds: (state) => {
        return state.ads.filter((ad) => ad.rendered);
    },

    // Get unrendered ads
    unrenderedAds: (state) => {
        return state.ads.filter((ad) => !ad.rendered);
    },

    // Get ad by ID with debugging
    getAdById: (state) => (id) => {
        // console.log(
        //     `Ads Store: Looking for ad with ID ${id}, available ads:`,
        //     state.ads.map((ad) => ({ id: ad.id, location: ad.location }))
        // );
        const foundAd = state.ads.find((ad) => ad.id === id);
        if (foundAd) {
            // console.log(`Ads Store: Found ad for ID ${id}:`, foundAd);
        } else {
            // console.log(`Ads Store: No ad found for ID ${id}`);
        }
        return foundAd;
    },

    // Get loading state for location
    isLocationLoading: (state) => (location) => {
        return state.loading[location] || false;
    },

    // Get error for location
    getLocationError: (state) => (location) => {
        return state.errors[location] || null;
    },

    // Check if location has ads
    hasAdsForLocation: (state) => (location) => {
        return state.ads.some((ad) => ad.location === location);
    },

    // Get entity configuration
    entityConfig: (state) => state.entityConfig,

    // Get fetch history
    fetchHistory: (state) => Array.from(state.fetchHistory),

    // Check if location was fetched
    wasLocationFetched: (state) => (location) => {
        const { entityType, entityId } = state.entityConfig;
        const fetchKey = `${entityType}_${entityId}_${location}`;
        return state.fetchHistory.has(fetchKey);
    },

    // Get ads count by location
    getAdsCountByLocation: (state) => (location) => {
        return state.ads.filter((ad) => ad.location === location).length;
    },

    getAdByLocation: (state) => (location) => {
        return state.ads.find((ad) => ad.location === location) || null;
    },

    getAdByLocationAndIndex: (state) => (location, index) => {
        return state.ads.find((ad) => ad.location === location && ad.index === index) || null;
    },

    getAdIdByLocation: (state, getters) => (location) => {
        const ad = getters.getAdByLocation(location);
        return ad ? ad.id : null;
    },

    // Returns the array of ad positions for the gallery, given resultMinCount and resultMaxCount
    galleryAdsPositions: (state, getters) => (resultMinCount, resultMaxCount) => {
        const galleryAdsCount = Math.min(getters.galleryAds.length, 2); // Cap at 2 ads for small sets
        const totalProperties = resultMaxCount - resultMinCount + 1;
        if (galleryAdsCount === 0 || totalProperties < 1) return [];

        // For small result sets, use hand-tuned positions for up to 2 ads
        if (totalProperties <= 7) {
            if (galleryAdsCount === 1) return [3];
            if (galleryAdsCount === 2) return [2, 5];
            return [];
        }

        // For larger result sets, spread as before
        const positions = [];
        const minStart = 3;
        const maxEnd = totalProperties - 1;
        const adsToShow = getters.galleryAds.length;
        if (adsToShow === 1) {
            positions.push(minStart);
        } else {
            for (let i = 0; i < adsToShow; i++) {
                const pos = Math.round(minStart + (i * (maxEnd - minStart)) / (adsToShow - 1));
                positions.push(pos);
            }
        }
        return Array.from(new Set(positions)).filter((p) => p >= minStart && p <= maxEnd);
    },

    // Returns the ad for a given index, using the above positions
    getGalleryAdForPosition: (state, getters) => (index, resultMinCount, resultMaxCount) => {
        const adsPositions = getters.galleryAdsPositions(resultMinCount, resultMaxCount);
        const galleryAds = getters.galleryAds;
        const positionInAds = adsPositions.indexOf(index);
        if (positionInAds !== -1 && galleryAds && galleryAds[positionInAds]) {
            return galleryAds[positionInAds];
        }
        return null;
    },

    getGalleryAdIndex: (state, getters) => (index) => {
        const galleryAds = getters.galleryAds;
        const positionInAds = galleryAds.indexOf(index);
        return positionInAds;
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
